
import React, { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import { Calculator, TrendingDown, Zap, DollarSign, Home, CheckCircle, AlertCircle } from 'lucide-react';

const CompetitorCalculator = () => {
  const [formData, setFormData] = useState({
    homeSize: '',
    rooms: '',
    features: '',
    budget: ''
  });

  const [results, setResults] = useState<any>(null);

  const calculateComparison = () => {
    const rooms = parseInt(formData.rooms) || 1;
    const baseCostPerRoom = 8000;

    // Calculate our pricing
    const ourBaseCost = rooms * baseCostPerRoom;
    const sizeFactor = formData.homeSize === 'large' ? 1.3 : formData.homeSize === 'medium' ? 1.0 : 0.8;
    const featuresFactor = formData.features === 'premium' ? 1.5 : formData.features === 'advanced' ? 1.2 : 1.0;

    const ourPrice = ourBaseCost * sizeFactor * featuresFactor;

    // Competitor pricing (30-50% higher)
    const competitor1Price = ourPrice * 1.35;
    const competitor2Price = ourPrice * 1.45;
    const competitor3Price = ourPrice * 1.52;

    // Calculate savings
    const avgCompetitorPrice = (competitor1Price + competitor2Price + competitor3Price) / 3;
    const savings = avgCompetitorPrice - ourPrice;
    const savingsPercentage = (savings / avgCompetitorPrice * 100).toFixed(1);

    // Features comparison
    const ourFeatures = [
    'Control local (sin dependencia de internet)',
    'Compatibilidad con 1000+ dispositivos',
    'Personalización completa',
    'Instalación profesional incluida',
    'Soporte técnico 24/7',
    'Garantía de 2 años',
    'Actualizaciones gratuitas',
    'Interfaz en español'];


    const competitorFeatures = [
    'Control por internet (requiere conexión)',
    'Compatibilidad limitada',
    'Configuración básica',
    'Instalación por separado',
    'Soporte limitado',
    'Garantía de 1 año',
    'Actualizaciones de pago',
    'Interfaz en inglés'];


    setResults({
      ourPrice,
      competitor1Price,
      competitor2Price,
      competitor3Price,
      avgCompetitorPrice,
      savings,
      savingsPercentage,
      ourFeatures,
      competitorFeatures
    });
  };

  const competitorData = [
  { name: 'Control4', factor: 1.45, features: 'Limitado' },
  { name: 'Crestron', factor: 1.52, features: 'Complejo' },
  { name: 'Savant', factor: 1.38, features: 'Básico' }];


  return (
    <section id="calculadora" className="py-20 bg-gradient-to-br from-blue-50 to-purple-50" data-id="buk0s3c8z" data-path="src/components/CompetitorCalculator.tsx">
      <div className="container mx-auto px-4" data-id="st4rcv40x" data-path="src/components/CompetitorCalculator.tsx">
        <div className="text-center mb-16" data-id="oeok1aqp1" data-path="src/components/CompetitorCalculator.tsx">
          <Badge className="bg-gradient-to-r from-green-600 to-blue-600 text-white px-4 py-2 text-sm font-medium mb-4" data-id="4xrn3lpqr" data-path="src/components/CompetitorCalculator.tsx">
            💰 Comparador de Precios
          </Badge>
          <h2 className="text-4xl lg:text-5xl font-bold text-gray-900 mb-6" data-id="pwx0iid5m" data-path="src/components/CompetitorCalculator.tsx">
            Compara Nuestros Precios
            <span className="bg-gradient-to-r from-green-600 to-blue-600 bg-clip-text text-transparent" data-id="y5288jf5p" data-path="src/components/CompetitorCalculator.tsx"> vs. Competencia</span>
          </h2>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto leading-relaxed" data-id="l7yovnvwk" data-path="src/components/CompetitorCalculator.tsx">
            Descubre por qué somos la opción más económica y funcional del mercado. 
            <span className="font-semibold text-green-600" data-id="jgeksdc3k" data-path="src/components/CompetitorCalculator.tsx"> Ahorra hasta 40% sin sacrificar calidad.</span>
          </p>
        </div>

        <div className="grid lg:grid-cols-2 gap-8 max-w-6xl mx-auto" data-id="inhe8tda9" data-path="src/components/CompetitorCalculator.tsx">
          {/* Calculator Form */}
          <Card className="shadow-2xl border-0" data-id="uvjxctgjw" data-path="src/components/CompetitorCalculator.tsx">
            <CardHeader data-id="8g6b3kcg8" data-path="src/components/CompetitorCalculator.tsx">
              <CardTitle className="flex items-center space-x-2 text-2xl" data-id="ffodr9g2h" data-path="src/components/CompetitorCalculator.tsx">
                <Calculator className="w-6 h-6 text-blue-600" data-id="a737lrfec" data-path="src/components/CompetitorCalculator.tsx" />
                <span data-id="s0su63yv3" data-path="src/components/CompetitorCalculator.tsx">Calculadora Comparativa</span>
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-6" data-id="jf9gxqqaj" data-path="src/components/CompetitorCalculator.tsx">
              <div className="space-y-2" data-id="hlyhwuphu" data-path="src/components/CompetitorCalculator.tsx">
                <Label htmlFor="homeSize" data-id="maibj66ve" data-path="src/components/CompetitorCalculator.tsx">Tamaño de tu hogar</Label>
                <Select value={formData.homeSize} onValueChange={(value) => setFormData({ ...formData, homeSize: value })} data-id="22scwkq7y" data-path="src/components/CompetitorCalculator.tsx">
                  <SelectTrigger data-id="4j11fwla1" data-path="src/components/CompetitorCalculator.tsx">
                    <SelectValue placeholder="Selecciona el tamaño" data-id="k2x8n50cs" data-path="src/components/CompetitorCalculator.tsx" />
                  </SelectTrigger>
                  <SelectContent data-id="xme9x7kqo" data-path="src/components/CompetitorCalculator.tsx">
                    <SelectItem value="small" data-id="k543wmv2u" data-path="src/components/CompetitorCalculator.tsx">Pequeño (&lt; 150 m²)</SelectItem>
                    <SelectItem value="medium" data-id="t8t422tqn" data-path="src/components/CompetitorCalculator.tsx">Mediano (150-300 m²)</SelectItem>
                    <SelectItem value="large" data-id="i73tjdxhl" data-path="src/components/CompetitorCalculator.tsx">Grande (&gt; 300 m²)</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2" data-id="ulzaiia1q" data-path="src/components/CompetitorCalculator.tsx">
                <Label htmlFor="rooms" data-id="902f48fpl" data-path="src/components/CompetitorCalculator.tsx">Número de habitaciones a automatizar</Label>
                <Input
                  id="rooms"
                  type="number"
                  placeholder="Ej: 4"
                  value={formData.rooms}
                  onChange={(e) => setFormData({ ...formData, rooms: e.target.value })} data-id="8tmhet3lb" data-path="src/components/CompetitorCalculator.tsx" />

              </div>

              <div className="space-y-2" data-id="48gkbwpwo" data-path="src/components/CompetitorCalculator.tsx">
                <Label htmlFor="features" data-id="i8r4x6b8g" data-path="src/components/CompetitorCalculator.tsx">Nivel de automatización</Label>
                <Select value={formData.features} onValueChange={(value) => setFormData({ ...formData, features: value })} data-id="432r35xr5" data-path="src/components/CompetitorCalculator.tsx">
                  <SelectTrigger data-id="jacerd9wa" data-path="src/components/CompetitorCalculator.tsx">
                    <SelectValue placeholder="Selecciona el nivel" data-id="gpqmcunmk" data-path="src/components/CompetitorCalculator.tsx" />
                  </SelectTrigger>
                  <SelectContent data-id="euy6v9sca" data-path="src/components/CompetitorCalculator.tsx">
                    <SelectItem value="basic" data-id="sukyhm7cu" data-path="src/components/CompetitorCalculator.tsx">Básico (Luces y persianas)</SelectItem>
                    <SelectItem value="advanced" data-id="6xzuxyt00" data-path="src/components/CompetitorCalculator.tsx">Avanzado (+ Clima y seguridad)</SelectItem>
                    <SelectItem value="premium" data-id="bnh2akbp9" data-path="src/components/CompetitorCalculator.tsx">Premium (+ Audio y control total)</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2" data-id="yqq954wnq" data-path="src/components/CompetitorCalculator.tsx">
                <Label htmlFor="budget" data-id="7q4nv4r8x" data-path="src/components/CompetitorCalculator.tsx">Presupuesto estimado (MXN)</Label>
                <Input
                  id="budget"
                  type="number"
                  placeholder="Ej: 100000"
                  value={formData.budget}
                  onChange={(e) => setFormData({ ...formData, budget: e.target.value })} data-id="cxhjjb3x8" data-path="src/components/CompetitorCalculator.tsx" />

              </div>

              <Button
                onClick={calculateComparison}
                className="w-full bg-gradient-to-r from-green-600 to-blue-600 hover:from-green-700 hover:to-blue-700 text-white py-3 text-lg font-medium" data-id="r9pp4trph" data-path="src/components/CompetitorCalculator.tsx">

                Comparar Precios
              </Button>
            </CardContent>
          </Card>

          {/* Results */}
          <div className="space-y-6" data-id="a01juvu9b" data-path="src/components/CompetitorCalculator.tsx">
            {results ?
            <>
                <Card className="shadow-2xl border-0 bg-gradient-to-r from-green-50 to-blue-50" data-id="8wb9yf0a9" data-path="src/components/CompetitorCalculator.tsx">
                  <CardContent className="p-6" data-id="gn7raugmk" data-path="src/components/CompetitorCalculator.tsx">
                    <h3 className="text-2xl font-bold text-gray-900 mb-6" data-id="escq5ea4l" data-path="src/components/CompetitorCalculator.tsx">💰 Comparación de Precios</h3>
                    
                    <div className="space-y-4" data-id="8p4sjwmlj" data-path="src/components/CompetitorCalculator.tsx">
                      <div className="flex justify-between items-center p-4 bg-blue-100 rounded-lg border-2 border-blue-200" data-id="s0zfz5vnf" data-path="src/components/CompetitorCalculator.tsx">
                        <div data-id="ohfo324mp" data-path="src/components/CompetitorCalculator.tsx">
                          <span className="text-gray-700 font-medium" data-id="3faqiltv5" data-path="src/components/CompetitorCalculator.tsx">Automatiza.me</span>
                          <div className="text-xs text-gray-500" data-id="i99cdmd1d" data-path="src/components/CompetitorCalculator.tsx">Tu mejor opción</div>
                        </div>
                        <span className="text-2xl font-bold text-blue-700" data-id="czopl2cai" data-path="src/components/CompetitorCalculator.tsx">${results.ourPrice.toLocaleString('es-MX')}</span>
                      </div>
                      
                      <div className="flex justify-between items-center p-4 bg-gray-100 rounded-lg" data-id="nd6z3czkr" data-path="src/components/CompetitorCalculator.tsx">
                        <div data-id="kdzp6h0ty" data-path="src/components/CompetitorCalculator.tsx">
                          <span className="text-gray-700" data-id="1q6g4uny4" data-path="src/components/CompetitorCalculator.tsx">Control4</span>
                          <div className="text-xs text-gray-500" data-id="cby7ajrz9" data-path="src/components/CompetitorCalculator.tsx">Competencia</div>
                        </div>
                        <span className="text-xl font-bold text-gray-600" data-id="ue4d7lqzm" data-path="src/components/CompetitorCalculator.tsx">${results.competitor1Price.toLocaleString('es-MX')}</span>
                      </div>
                      
                      <div className="flex justify-between items-center p-4 bg-gray-100 rounded-lg" data-id="nb5mngrzg" data-path="src/components/CompetitorCalculator.tsx">
                        <div data-id="851wm7smv" data-path="src/components/CompetitorCalculator.tsx">
                          <span className="text-gray-700" data-id="i7w4k5fe1" data-path="src/components/CompetitorCalculator.tsx">Crestron</span>
                          <div className="text-xs text-gray-500" data-id="368zqlle0" data-path="src/components/CompetitorCalculator.tsx">Competencia</div>
                        </div>
                        <span className="text-xl font-bold text-gray-600" data-id="2wm60tfxx" data-path="src/components/CompetitorCalculator.tsx">${results.competitor2Price.toLocaleString('es-MX')}</span>
                      </div>
                      
                      <div className="flex justify-between items-center p-4 bg-gray-100 rounded-lg" data-id="uv64srjh6" data-path="src/components/CompetitorCalculator.tsx">
                        <div data-id="7twag8d7h" data-path="src/components/CompetitorCalculator.tsx">
                          <span className="text-gray-700" data-id="yrpjvq4ww" data-path="src/components/CompetitorCalculator.tsx">Savant</span>
                          <div className="text-xs text-gray-500" data-id="jrxzj0xhk" data-path="src/components/CompetitorCalculator.tsx">Competencia</div>
                        </div>
                        <span className="text-xl font-bold text-gray-600" data-id="idxt647rf" data-path="src/components/CompetitorCalculator.tsx">${results.competitor3Price.toLocaleString('es-MX')}</span>
                      </div>
                      
                      <div className="flex justify-between items-center p-4 bg-green-100 rounded-lg border-2 border-green-200" data-id="clk91xxy1" data-path="src/components/CompetitorCalculator.tsx">
                        <div data-id="rq7oqo6h3" data-path="src/components/CompetitorCalculator.tsx">
                          <span className="text-gray-700 font-medium" data-id="18oa7ujb4" data-path="src/components/CompetitorCalculator.tsx">Tu ahorro con nosotros</span>
                          <div className="text-xs text-gray-500" data-id="0eg3vok4e" data-path="src/components/CompetitorCalculator.tsx">{results.savingsPercentage}% menos</div>
                        </div>
                        <span className="text-3xl font-bold text-green-700" data-id="el3g8e9cc" data-path="src/components/CompetitorCalculator.tsx">${results.savings.toLocaleString('es-MX')}</span>
                      </div>
                    </div>
                  </CardContent>
                </Card>

                <Card className="shadow-2xl border-0" data-id="aoxk3juku" data-path="src/components/CompetitorCalculator.tsx">
                  <CardContent className="p-6" data-id="t8pgr0qwb" data-path="src/components/CompetitorCalculator.tsx">
                    <h3 className="text-2xl font-bold text-gray-900 mb-6" data-id="qs0cd984g" data-path="src/components/CompetitorCalculator.tsx">🔥 Comparación de Características</h3>
                    
                    <div className="grid md:grid-cols-2 gap-6" data-id="n53dbmor8" data-path="src/components/CompetitorCalculator.tsx">
                      <div className="space-y-3" data-id="s9qy8k00b" data-path="src/components/CompetitorCalculator.tsx">
                        <h4 className="font-semibold text-green-600 text-lg" data-id="zp4qmjkrr" data-path="src/components/CompetitorCalculator.tsx">✅ Automatiza.me</h4>
                        {results.ourFeatures.map((feature: string, index: number) =>
                      <div key={index} className="flex items-center space-x-2" data-id="itiw3fr20" data-path="src/components/CompetitorCalculator.tsx">
                            <CheckCircle className="w-4 h-4 text-green-600 flex-shrink-0" data-id="wfoydh0ra" data-path="src/components/CompetitorCalculator.tsx" />
                            <span className="text-sm text-gray-700" data-id="qb4q7ub5l" data-path="src/components/CompetitorCalculator.tsx">{feature}</span>
                          </div>
                      )}
                      </div>
                      
                      <div className="space-y-3" data-id="kmt55b7at" data-path="src/components/CompetitorCalculator.tsx">
                        <h4 className="font-semibold text-red-600 text-lg" data-id="351up7vbp" data-path="src/components/CompetitorCalculator.tsx">❌ Competencia</h4>
                        {results.competitorFeatures.map((feature: string, index: number) =>
                      <div key={index} className="flex items-center space-x-2" data-id="wztwz0bio" data-path="src/components/CompetitorCalculator.tsx">
                            <AlertCircle className="w-4 h-4 text-red-600 flex-shrink-0" data-id="rkhadpup5" data-path="src/components/CompetitorCalculator.tsx" />
                            <span className="text-sm text-gray-700" data-id="gifcd0b8n" data-path="src/components/CompetitorCalculator.tsx">{feature}</span>
                          </div>
                      )}
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </> :

            <Card className="shadow-2xl border-0" data-id="6am263nx6" data-path="src/components/CompetitorCalculator.tsx">
                <CardContent className="p-8 text-center" data-id="tqrqvtlci" data-path="src/components/CompetitorCalculator.tsx">
                  <div className="text-6xl mb-4" data-id="vbcfwdv0j" data-path="src/components/CompetitorCalculator.tsx">🏠</div>
                  <h3 className="text-2xl font-bold text-gray-900 mb-4" data-id="zemurgnqc" data-path="src/components/CompetitorCalculator.tsx">
                    Por Qué Elegir Automatiza.me
                  </h3>
                  <div className="space-y-4" data-id="ek915xfs2" data-path="src/components/CompetitorCalculator.tsx">
                    <div className="flex items-center space-x-3 p-3 bg-gray-50 rounded-lg" data-id="0ubctzms9" data-path="src/components/CompetitorCalculator.tsx">
                      <div className="text-blue-600" data-id="ruvpo1ok1" data-path="src/components/CompetitorCalculator.tsx">
                        <DollarSign className="w-5 h-5" data-id="jtlvk8ut2" data-path="src/components/CompetitorCalculator.tsx" />
                      </div>
                      <span className="text-gray-700" data-id="ahf0hc0ro" data-path="src/components/CompetitorCalculator.tsx">Precios hasta 40% más económicos</span>
                    </div>
                    <div className="flex items-center space-x-3 p-3 bg-gray-50 rounded-lg" data-id="sny4u6vuz" data-path="src/components/CompetitorCalculator.tsx">
                      <div className="text-blue-600" data-id="lw8u7zlqy" data-path="src/components/CompetitorCalculator.tsx">
                        <Zap className="w-5 h-5" data-id="9ls3upvbr" data-path="src/components/CompetitorCalculator.tsx" />
                      </div>
                      <span className="text-gray-700" data-id="jd8c8b0gy" data-path="src/components/CompetitorCalculator.tsx">Más funciones y compatibilidad</span>
                    </div>
                    <div className="flex items-center space-x-3 p-3 bg-gray-50 rounded-lg" data-id="g0jr3rhab" data-path="src/components/CompetitorCalculator.tsx">
                      <div className="text-blue-600" data-id="5s8lcoa35" data-path="src/components/CompetitorCalculator.tsx">
                        <Home className="w-5 h-5" data-id="pj50bf1o2" data-path="src/components/CompetitorCalculator.tsx" />
                      </div>
                      <span className="text-gray-700" data-id="mcqsbhk0s" data-path="src/components/CompetitorCalculator.tsx">Soporte técnico especializado</span>
                    </div>
                  </div>
                </CardContent>
              </Card>
            }
          </div>
        </div>

        {/* Why We're Better */}
        <div className="mt-16 bg-white rounded-2xl p-8 shadow-xl" data-id="i8js8at0j" data-path="src/components/CompetitorCalculator.tsx">
          <div className="text-center mb-8" data-id="wlfatmf6v" data-path="src/components/CompetitorCalculator.tsx">
            <h3 className="text-3xl font-bold text-gray-900 mb-4" data-id="n1pgrejgh" data-path="src/components/CompetitorCalculator.tsx">
              🎯 Por Qué Somos Mejor Opción
            </h3>
            <p className="text-gray-600 max-w-2xl mx-auto" data-id="kf0yz5b0v" data-path="src/components/CompetitorCalculator.tsx">
              No solo ofrecemos mejores precios, sino también mejor funcionalidad y soporte.
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-6" data-id="tf5gdbdj0" data-path="src/components/CompetitorCalculator.tsx">
            <div className="text-center" data-id="lulnkhnwu" data-path="src/components/CompetitorCalculator.tsx">
              <div className="bg-blue-100 rounded-full p-4 w-16 h-16 mx-auto mb-4 flex items-center justify-center" data-id="xad2dgkzv" data-path="src/components/CompetitorCalculator.tsx">
                <DollarSign className="w-8 h-8 text-blue-600" data-id="aknlwsrwt" data-path="src/components/CompetitorCalculator.tsx" />
              </div>
              <h4 className="font-semibold text-gray-900 mb-2" data-id="fs3akaw9s" data-path="src/components/CompetitorCalculator.tsx">30-40% Más Económico</h4>
              <p className="text-sm text-gray-600" data-id="y1353q1f5" data-path="src/components/CompetitorCalculator.tsx">
                Misma calidad, mejores precios. Ahorra miles de pesos sin comprometer funcionalidad.
              </p>
            </div>

            <div className="text-center" data-id="ck25k2zhr" data-path="src/components/CompetitorCalculator.tsx">
              <div className="bg-green-100 rounded-full p-4 w-16 h-16 mx-auto mb-4 flex items-center justify-center" data-id="xfo33al2e" data-path="src/components/CompetitorCalculator.tsx">
                <Zap className="w-8 h-8 text-green-600" data-id="3tcxpw7lt" data-path="src/components/CompetitorCalculator.tsx" />
              </div>
              <h4 className="font-semibold text-gray-900 mb-2" data-id="14y5nl5v1" data-path="src/components/CompetitorCalculator.tsx">Más Funcional</h4>
              <p className="text-sm text-gray-600" data-id="bcnp77jwt" data-path="src/components/CompetitorCalculator.tsx">
                Control local, mayor compatibilidad y personalización completa de tu sistema.
              </p>
            </div>

            <div className="text-center" data-id="dm6wrs176" data-path="src/components/CompetitorCalculator.tsx">
              <div className="bg-purple-100 rounded-full p-4 w-16 h-16 mx-auto mb-4 flex items-center justify-center" data-id="sk785a20c" data-path="src/components/CompetitorCalculator.tsx">
                <CheckCircle className="w-8 h-8 text-purple-600" data-id="osthvppoz" data-path="src/components/CompetitorCalculator.tsx" />
              </div>
              <h4 className="font-semibold text-gray-900 mb-2" data-id="46ct9cvz2" data-path="src/components/CompetitorCalculator.tsx">Mejor Soporte</h4>
              <p className="text-sm text-gray-600" data-id="gbfbts1st" data-path="src/components/CompetitorCalculator.tsx">
                Soporte técnico en español, 24/7, con técnicos especializados y garantía extendida.
              </p>
            </div>
          </div>
        </div>
      </div>
    </section>);

};

export default CompetitorCalculator;